import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * A copy-to-clipboard button (clipboard API, with a hidden-textarea fallback for non-secure
 * contexts). Shared by CopyableId / CopyableEmail and anything else that needs copy-on-click.
 */
export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(t);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard API needs a secure context; fall back to a hidden textarea + execCommand.
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
      } catch {
        /* nothing more we can do — leave the value in the tooltip for manual copy */
      }
      document.body.removeChild(ta);
    }
  }

  return (
    <button
      type="button"
      className={"id-copy" + (copied ? " copied" : "")}
      // stopPropagation: these sit inside clickable table rows — copying must not open the row.
      onClick={(e) => { e.stopPropagation(); void copy(); }}
      aria-label={copied ? "Copied" : label}
      title={copied ? "Copied" : label}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
}

/**
 * A monospace id rendered cropped-with-ellipsis plus a copy button.
 * User ids aren't always GUIDs — a long unbroken hash would otherwise blow the table width and
 * push later columns outside the border — so the display is cropped and the full value is reachable
 * via the copy button (and the hover tooltip). Short ids show in full with no ellipsis.
 */
export function CopyableId({ value }: { value: string }) {
  return (
    <span className="id-cell">
      <span className="id-text mono" title={value}>{value}</span>
      <CopyButton value={value} label="Copy id" />
    </span>
  );
}

/** An email address with a copy button. Renders a faint "(none)" when there's no email.
 *  With `linkTo`, the address becomes an in-app link (e.g. a cross-link to the Users page). */
export function CopyableEmail({ email, linkTo }: { email: string | null | undefined; linkTo?: string }) {
  if (!email) return <span className="faint">(none)</span>;
  return (
    <span className="id-cell">
      {linkTo ? (
        // stopPropagation: like the copy button, these sit inside clickable table rows.
        <Link to={linkTo} title={`${email} — open in Users`} onClick={(e) => e.stopPropagation()}>
          {email}
        </Link>
      ) : (
        <span title={email}>{email}</span>
      )}
      <CopyButton value={email} label="Copy email" />
    </span>
  );
}

function CopyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" />
      <path d="M5 15H4V4h11v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true">
      <path d="M5 13l4 4L19 6" />
    </svg>
  );
}
